import { useState } from 'react';
import { useScrollAnimation } from '../../hooks/useScrollAnimation';
import { Building2, User, Briefcase, Users } from 'lucide-react';

const cases = {
  empresa: {
    overline: 'Caso Empresa',
    context: [
      { label: 'Sector', value: 'Industrial' },
      { label: 'Equipo', value: '60 personas' },
      { label: 'Facturación', value: '12M€' },
    ],
    challenge: 'Necesita experiencia en expansión internacional para tomar decisiones con mayor criterio y menor riesgo.',
    process: 'Diagnóstico en menos de 48 horas. Identificación de 3 perfiles con experiencia directa en internacionalización del sector.',
    result: 'Hoja de ruta estratégica y acompañamiento en ejecución con un profesional verificado.',
    comparison: {
      title: 'Ejemplo orientativo de ahorro',
      traditional: {
        title: 'Directivo en plantilla a jornada completa',
        label: 'Coste real estimado',
        items: [
          { name: 'Salario bruto anual', value: '70.000 €' },
          { name: 'Seguridad social (empresa 33%)', value: '23.000 €' },
          { name: 'Variable / bonus (30%)', value: '21.000 €' },
          { name: 'Formación, dietas, beneficios', value: '4.000 €' },
        ],
        extras: [
          { name: 'Compromiso mínimo', value: '12 meses' },
          { name: 'Disponibilidad real', value: '1.800 h/año' },
        ],
        total: '118.000 €/año',
        note: 'Sin contar costes de selección, onboarding ni posible indemnización.',
      },
      conadvy: {
        title: 'Advisor senior por horas, sin estructura fija',
        label: 'Solo lo que necesita',
        items: [
          { name: 'Tarifa orientativa por hora', value: '150 €/h' },
          { name: 'Horas estimadas al mes', value: '14 h' },
          { name: 'Coste mensual aproximado', value: '2.100 €' },
          { name: 'Seguridad social', value: '0 €' },
          { name: 'Compromisos fijos', value: 'Ninguno' },
        ],
        extras: [
          { name: 'Acceso al perfil adecuado', value: 'En 48 h' },
          { name: 'Flexibilidad', value: 'Total' },
        ],
        total: '25.200 €/año',
        note: 'Estimación orientativa. Las tarifas varían según perfil y dedicación.',
      },
    },
  },
  profesional: {
    overline: 'Caso Profesional',
    context: [
      { label: 'Perfil', value: 'Ex-CEO' },
      { label: 'Experiencia', value: '28 años' },
      { label: 'Sectores', value: 'Industria, Energía' },
    ],
    challenge: 'Sin canal estructurado para poner su experiencia y criterio al servicio de empresas que lo necesitan.',
    process: 'Onboarding validado. Evaluación profesional. Acceso a oportunidades cualificadas y alineadas con su perfil.',
    result: 'Impacto real, posicionamiento profesional y experiencia compensada.',
    comparison: {
      title: 'Cómo se accede hoy al advisory',
      traditional: {
        title: 'Vías tradicionales de acceso',
        label: 'Limitaciones habituales',
        items: [
          { name: 'Red personal de contactos', value: 'Limitada' },
          { name: 'Alcance geográfico y sectorial', value: 'Reducido' },
          { name: 'Consultora de RRHH / headhunter', value: '~15–25% fee' },
          { name: 'Control sobre el proceso', value: 'Bajo' },
        ],
        extras: [
          { name: 'Visibilidad ante empresas', value: 'Pasiva' },
          { name: 'Oportunidades alineadas', value: 'Escasas' },
        ],
        total: 'Alto coste, bajo alcance',
        note: 'Las vías tradicionales dependen de quién te conoce, no de lo que puedes aportar.',
      },
      conadvy: {
        title: 'Modelo Conadvy para el profesional',
        label: 'Mayor alcance, menor coste',
        items: [
          { name: 'Acceso a empresas verificadas', value: 'Directo' },
          { name: 'Alcance geográfico y sectorial', value: 'Amplio' },
          { name: 'Coste de intermediación', value: 'Muy inferior' },
          { name: 'Control sobre mandatos', value: 'Total' },
        ],
        extras: [
          { name: 'Visibilidad ante empresas', value: 'Activa' },
          { name: 'Matching por criterio profesional', value: 'Personalizado' },
        ],
        total: 'Menor coste, mayor alcance',
        note: 'Conadvy conecta al perfil adecuado con la necesidad concreta. A mayor volumen de calidad en ambos lados, mayor precisión del matching.',
      },
    },
  },
};

function CostComparison({ data }) {
  const { traditional: t, conadvy: c } = data;
  return (
    <div className="mt-10">
      <p className="text-[#00163E]/40 text-xs uppercase tracking-wider mb-4">{data.title}</p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      {/* Traditional */}
      <div className="bg-white border border-[#00163E]/10 rounded-sm p-6 md:p-8 flex flex-col h-full">
        <div className="flex items-center gap-3 mb-4">
          <Briefcase size={16} className="text-[#00163E]/40" strokeWidth={1.5} />
          <span className="text-[#00163E]/40 text-xs uppercase tracking-wider">Modelo Tradicional</span>
        </div>
        <h4 className="text-[#00163E] text-base font-medium mb-5">{t.title}</h4>
        <span className="text-[#00163E]/50 text-xs uppercase tracking-wider">{t.label}</span>
        <div className="mt-3 space-y-2.5">
          {t.items.map((item) => (
            <div key={item.name} className="flex justify-between text-sm">
              <span className="text-[#00163E]/60">{item.name}</span>
              <span className="text-[#00163E] font-medium">{item.value}</span>
            </div>
          ))}
        </div>
        <div className="h-[1px] bg-[#00163E]/10 my-4" />
        <div className="space-y-2">
          {t.extras.map((item) => (
            <div key={item.name} className="flex justify-between text-sm">
              <span className="text-[#00163E]/60">{item.name}</span>
              <span className="text-[#00163E] font-medium">{item.value}</span>
            </div>
          ))}
        </div>
        <div className="mt-auto pt-5">
          <div className="bg-[#00163E]/5 rounded-sm p-4">
            <span className="text-[#00163E]/40 text-xs uppercase tracking-wider block mb-1">Coste total anual estimado</span>
            <span className="text-[#00163E] text-3xl font-medium">{t.total}</span>
          </div>
          <p className="text-[#8A9BB1] text-xs mt-3 leading-relaxed">{t.note}</p>
        </div>
      </div>

      {/* Conadvy */}
      <div className="bg-white border border-[#C58C40]/30 rounded-sm p-6 md:p-8 relative flex flex-col h-full">
        <div className="flex items-center gap-3 mb-4">
          <Users size={16} className="text-[#C58C40]" strokeWidth={1.5} />
          <span className="text-[#C58C40] text-xs uppercase tracking-wider font-medium">Modelo Conadvy</span>
        </div>
        <h4 className="text-[#00163E] text-base font-medium mb-5">{c.title}</h4>
        <span className="text-[#C58C40] text-xs uppercase tracking-wider">{c.label}</span>
        <div className="mt-3 space-y-2.5">
          {c.items.map((item) => (
            <div key={item.name} className="flex justify-between text-sm">
              <span className="text-[#00163E]/60">{item.name}</span>
              <span className="text-[#00163E] font-medium">{item.value}</span>
            </div>
          ))}
        </div>
        <div className="h-[1px] bg-[#00163E]/10 my-4" />
        <div className="space-y-2">
          {c.extras.map((item) => (
            <div key={item.name} className="flex justify-between text-sm">
              <span className="text-[#00163E]/60">{item.name}</span>
              <span className="text-[#00163E] font-medium">{item.value}</span>
            </div>
          ))}
        </div>
        <div className="mt-auto pt-5">
          <div className="bg-[#C58C40]/5 border border-[#C58C40]/15 rounded-sm p-4">
            <span className="text-[#C58C40]/70 text-xs uppercase tracking-wider block mb-1">Coste total anual estimado</span>
            <span className="text-[#C58C40] text-2xl font-medium">{c.total}</span>
          </div>
          <p className="text-[#8A9BB1] text-xs mt-3 leading-relaxed">{c.note}</p>
        </div>
      </div>
    </div>
    </div>
  );
}

export default function UseCases() {
  const sectionRef = useScrollAnimation();
  const [active, setActive] = useState('empresa');
  const c = cases[active];

  return (
    <section
      ref={sectionRef}
      data-testid="use-cases-section"
      className="bg-[#F5F5F3] grain-light py-32 md:py-40 relative"
    >
      <div className="max-w-6xl mx-auto px-6 md:px-12">
        <div className="fade-up mb-16 md:mb-20">
          <span className="text-[#C58C40] text-xs font-medium tracking-[0.2em] uppercase block mb-6">
            Impacto en la práctica
          </span>
          <h2
            data-testid="use-cases-headline"
            className="text-3xl md:text-4xl font-normal tracking-[0.01em] leading-snug text-[#00163E] max-w-2xl"
          >
            Cada mandato es diferente. Así es como funciona.
          </h2>
        </div>

        <div className="fade-up fade-up-delay-1 flex gap-1 mb-12 bg-[#00163E]/5 rounded-sm p-1 w-fit">
          <button
            onClick={() => setActive('empresa')}
            data-testid="usecase-tab-empresa"
            className={`flex items-center gap-2.5 px-6 py-3 rounded-sm text-sm font-medium transition-all duration-300 ${
              active === 'empresa' ? 'bg-[#00163E] text-white shadow-sm' : 'text-[#00163E]/50 hover:text-[#00163E]/80'
            }`}
          >
            <Building2 size={16} strokeWidth={1.5} />
            <span>Empresa</span>
          </button>
          <button
            onClick={() => setActive('profesional')}
            data-testid="usecase-tab-profesional"
            className={`flex items-center gap-2.5 px-6 py-3 rounded-sm text-sm font-medium transition-all duration-300 ${
              active === 'profesional' ? 'bg-[#00163E] text-white shadow-sm' : 'text-[#00163E]/50 hover:text-[#00163E]/80'
            }`}
          >
            <User size={16} strokeWidth={1.5} />
            <span>Advisor</span>
          </button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 usecase-content-enter" key={active}>
          <div className="lg:col-span-4">
            <div className="bg-white border border-[#00163E]/10 rounded-sm p-8 md:p-10">
              <span className="text-[#C58C40] text-xs font-medium tracking-[0.2em] uppercase block mb-6">{c.overline}</span>
              <div className="space-y-5">
                {c.context.map((item) => (
                  <div key={item.label}>
                    <p className="text-[#00163E]/40 text-xs uppercase tracking-wider mb-1">{item.label}</p>
                    <p className="text-[#00163E] text-base font-medium">{item.value}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="lg:col-span-8 flex flex-col justify-between">
            <div className="mb-10">
              <p className="text-[#00163E]/40 text-xs uppercase tracking-wider mb-3">Desafío</p>
              <p className="text-[#00163E] text-xl md:text-2xl font-normal leading-relaxed">{c.challenge}</p>
            </div>
            <div className="h-[1px] w-full bg-[#00163E]/10 mb-10" />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
              <div>
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-2 h-2 rounded-full bg-[#C58C40]" />
                  <p className="text-[#00163E]/40 text-xs uppercase tracking-wider">Proceso Conadvy</p>
                </div>
                <p className="text-[#00163E]/70 text-base leading-relaxed">{c.process}</p>
              </div>
              <div>
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-2 h-2 rounded-full bg-[#00163E]" />
                  <p className="text-[#00163E]/40 text-xs uppercase tracking-wider">Resultado</p>
                </div>
                <p className="text-[#00163E]/70 text-base leading-relaxed">{c.result}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Cost Comparison */}
        <div className="usecase-content-enter" key={`comp-${active}`}>
          <CostComparison data={c.comparison} />
        </div>
      </div>
    </section>
  );
}
